import React, { useEffect, useState } from "react";
import { Button, Modal, Table, TableColumnsType, Tag, Alert, Space } from "antd";
import styled from "@emotion/styled";
import useHttpClient from "components/hooks/useHttpClient";
import { useProductVariantsStore } from "stores/productVariantsStore";
import { useLocaleStore } from "stores/localeStore";
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { IProductItem } from "interfaces";

interface VariantNameValidationModalProps {
  onClose: () => void;
  isOpen: boolean;
}

interface VariantTranslation {
  variantId: number;
  variantItemNo: string;
  languageId: number;
  languageName: string;
  name: string;
}

interface ValidationResult {
  languageId: number;
  languageName: string;
  isUnique: boolean;
  duplicates: Array<{
    variantId: number;
    variantItemNo: string;
    name: string;
  }>;
}

const TableWrapper = styled.div`
  .ant-table-cell {
    padding: 12px 8px !important;
  }
`;

const VariantNameValidationModal: React.FC<VariantNameValidationModalProps> = ({
  onClose,
  isOpen,
}) => {
  const { productVariants } = useProductVariantsStore();
  const { languages } = useLocaleStore();
  const { makeHttpRequest } = useHttpClient();
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [variantTranslations, setVariantTranslations] = useState<VariantTranslation[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  const variantIds = productVariants?.map((v) => v.id) || [];

  const getLanguageName = (languageId: number): string => {
    const language = languages.find((lang) => lang.id === languageId);
    if (!language) return `Language ${languageId}`;
    
    switch (language.locale) {
      case 'sv':
        return 'Svenska';
      case 'da':
        return 'Danska';
      case 'no':
        return 'Norska';
      default:
        return language.locale.toUpperCase();
    }
  };

  useEffect(() => {
    if (!isOpen || variantIds.length === 0 || languages.length === 0) {
      return;
    }

    const fetchVariantsForAllLanguages = async () => {
      setIsLoading(true);
      setHasErrors(false);
      setValidationResults([]);
      setVariantTranslations([]);

      try {
        // Fetch variants for each language separately
        const fetchPromises = languages.map(async (language) => {
          try {
            // Build the URL with variant IDs as query parameters
            // Format: /productItems?ids=21455&ids=21456&_end=10
            const idsParams = variantIds.map(id => `ids=${id}`).join('&');
            const response = await makeHttpRequest(
              "GET",
              `/productItems?${idsParams}&_end=${variantIds.length}`,
              null,
              { lang: language.locale }
            );

            if (response?.errorObject) {
              console.error(`Error fetching variants for language ${language.locale}:`, response.errorObject);
              return null;
            }

            // Extract variant data from response
            // The response structure may vary - check both data and items
            const variantsData = response?.data || response?.items || [];
            
            // Map to VariantTranslation format
            return variantsData.map((variantData: any) => {
              const variant = productVariants.find((v) => v.id === variantData?.id);
              if (!variant) return null;

              return {
                variantId: variant.id,
                variantItemNo: variant.itemNo,
                languageId: language.id,
                languageName: getLanguageName(language.id),
                name: variantData?.name || "",
              };
            }).filter(Boolean) as VariantTranslation[];
          } catch (error) {
            console.error(`Error fetching variants for language ${language.locale}:`, error);
            return null;
          }
        });

        const results = await Promise.all(fetchPromises);
        const allTranslations = results
          .filter((result) => result !== null)
          .flat() as VariantTranslation[];

        setVariantTranslations(allTranslations);

        // Validate uniqueness per language
        validateUniqueness(allTranslations);
      } catch (error) {
        console.error("Error fetching variant translations:", error);
        setHasErrors(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVariantsForAllLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, variantIds.length, languages.length, refreshTrigger]);

  const validateUniqueness = (translations: VariantTranslation[]) => {
    // Group translations by language
    const translationsByLanguage = translations.reduce((acc, translation) => {
      if (!acc[translation.languageId]) {
        acc[translation.languageId] = [];
      }
      acc[translation.languageId].push(translation);
      return acc;
    }, {} as Record<number, VariantTranslation[]>);

    // Check for duplicates in each language
    const results: ValidationResult[] = Object.entries(translationsByLanguage).map(
      ([languageIdStr, langTranslations]) => {
        const languageId = parseInt(languageIdStr);
        const languageName = langTranslations[0]?.languageName || getLanguageName(languageId);

        // Group by name (case-insensitive, trimmed)
        const nameGroups = langTranslations.reduce((acc, translation) => {
          const normalizedName = (translation.name || "").trim().toLowerCase();
          if (!acc[normalizedName]) {
            acc[normalizedName] = [];
          }
          acc[normalizedName].push(translation);
          return acc;
        }, {} as Record<string, VariantTranslation[]>);

        // Find duplicates (names that appear more than once)
        const duplicates: Array<{
          variantId: number;
          variantItemNo: string;
          name: string;
        }> = [];

        Object.entries(nameGroups).forEach(([normalizedName, group]) => {
          if (group.length > 1 && normalizedName !== "") {
            // Add all variants with this duplicate name
            group.forEach((translation) => {
              duplicates.push({
                variantId: translation.variantId,
                variantItemNo: translation.variantItemNo,
                name: translation.name,
              });
            });
          }
        });

        return {
          languageId,
          languageName,
          isUnique: duplicates.length === 0,
          duplicates,
        };
      }
    );

    setValidationResults(results);
    setHasErrors(results.some((result) => !result.isUnique));
  };


  const columns: TableColumnsType<ValidationResult> = [
    {
      title: "Språk",
      dataIndex: "languageName",
      key: "languageName",
      width: 150,
      render: (text: string, record: ValidationResult) => (
        <Space>
          {record.isUnique ? (
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
          ) : (
            <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
          )}
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isUnique",
      key: "isUnique",
      width: 120,
      render: (isUnique: boolean) => (
        <Tag color={isUnique ? "success" : "error"}>
          {isUnique ? "Unika namn" : "Duplicerade namn"}
        </Tag>
      ),
    },
    {
      title: "Duplicerade varianter",
      dataIndex: "duplicates",
      key: "duplicates",
      render: (duplicates: Array<{ variantId: number; variantItemNo: string; name: string }>) => {
        if (duplicates.length === 0) {
          return <span style={{ color: "#52c41a" }}>Inga dupliceringar</span>;
        }

        // Group duplicates by name
        const groupedByName = duplicates.reduce((acc, dup) => {
          if (!acc[dup.name]) {
            acc[dup.name] = [];
          }
          acc[dup.name].push(dup);
          return acc;
        }, {} as Record<string, typeof duplicates>);

        return (
          <div>
            {Object.entries(groupedByName).map(([name, group]) => (
              <div key={name} style={{ marginBottom: "8px" }}>
                <strong>"{name}"</strong> finns i:
                <ul style={{ margin: "4px 0 0 20px", padding: 0 }}>
                  {group.map((dup) => (
                    <li key={dup.variantId}>
                      Variant {dup.variantItemNo} (ID: {dup.variantId})
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  const allVariantsTableColumns: TableColumnsType<VariantTranslation> = [
    {
      title: "Variant",
      dataIndex: "variantItemNo",
      key: "variantItemNo",
      width: 120,
    },
    {
      title: "Språk",
      dataIndex: "languageName",
      key: "languageName",
      width: 120,
    },
    {
      title: "Namn",
      dataIndex: "name",
      key: "name",
    },
  ];

  return (
    <Modal
      title="Validera variantnamn"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Stäng
        </Button>,
        <Button 
          key="refresh" 
          type="primary" 
          onClick={() => {
            // Trigger re-fetch by incrementing refreshTrigger
            setRefreshTrigger((prev) => prev + 1);
          }} 
          loading={isLoading}
          disabled={!isOpen || variantIds.length === 0}
        >
          Uppdatera
        </Button>,
      ]}
      width={900}
    >
      {isLoading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <LoadingOutlined style={{ fontSize: 24 }} />
          <p style={{ marginTop: "16px" }}>Hämtar variantdata...</p>
        </div>
      ) : hasErrors ? (
        <Alert
          message="Duplicerade namn hittades"
          description="Några varianter har samma namn inom samma språk vilket är otillåtet för export till Norce. Se tabellen nedan för detaljer. Export till Norce av produkten kommer misslyckas för all språk."
          type="error"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      ) : validationResults.length > 0 && validationResults.every((r) => r.isUnique) ? (
        <Alert
          message="Alla namn är unika"
          description="Alla variantnamn är unika inom sina respektive språk."
          type="success"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      ) : null}

      {validationResults.length > 0 && (
        <>
          <h3 style={{ marginTop: "16px", marginBottom: "12px" }}>Valideringsresultat</h3>
          <TableWrapper>
            <Table
              columns={columns}
              dataSource={validationResults}
              pagination={false}
              rowKey="languageId"
              size="small"
            />
          </TableWrapper>
        </>
      )}

      {variantTranslations.length > 0 && (
        <>
          <h3 style={{ marginTop: "24px", marginBottom: "12px" }}>Alla variantnamn</h3>
          <TableWrapper>
            <Table
              columns={allVariantsTableColumns}
              dataSource={variantTranslations}
              pagination={false}
              rowKey={(record) => `${record.variantId}-${record.languageId}`}
              size="small"
              scroll={{ y: 300 }}
            />
          </TableWrapper>
        </>
      )}

      {!isLoading && productVariants && productVariants.length === 0 && (
        <Alert
          message="Inga varianter"
          description="Det finns inga varianter att validera."
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};

export default VariantNameValidationModal;
